import { action, makeObservable, observable } from 'mobx';
import Router from 'next/router';
import { EnterFlowProgress } from 'types/ui/EnterFlowProgress';
import { HeaderBanner } from 'types/ui/HeaderBanner';
import { Toast } from 'types/ui/Toast';
import { RootStore } from './RootStore';

export type UIHydration = {
  toast?: Toast;
  headerBanner?: HeaderBanner;
  drawCounterInHeader?: boolean;
};

export const DASHBOARD_LINK = '/dashboard';

const UI_STORE_KEY_FOR_WINDOW_OBJECT = 'daymadeUIStore';

const ACCOUNT_PAGE_LINK = '/account';

export class UIStore {
  root: RootStore;
  toast: Toast | null = null;
  headerBanner: HeaderBanner | null = null;
  drawCounterInHeader: boolean = false;
  enterFlowProgress: EnterFlowProgress | null = null;
  showVideoOverlay: boolean = false;
  videoOverlayType: 'instagram' | 'youtube' | null = null;
  videoOverlayLink: string | null = null;
  zendeskChatBotHiddenReasons: Record<string, true> = {};

  private toastTimeout?: ReturnType<typeof setTimeout>;

  constructor(root: RootStore) {
    this.root = root;
    // eslint-disable-next-line mobx/exhaustive-make-observable
    makeObservable(this, {
      displayToast: action,
      displayHeaderBanner: action,
      toast: observable,
      headerBanner: observable,
      drawCounterInHeader: observable,
      enterFlowProgress: observable,
      videoOverlayLink: observable,
      videoOverlayType: observable,
      showVideoOverlay: observable,
      zendeskChatBotHiddenReasons: observable,
      showDrawCounterInHeader: action,
      setEnterFlowProgress: action,
      setShowVideoOverlay: action,
      setVideoOverlayLink: action,
      removeVideoOverlayLink: action,
      navigateToDashboard: action,
      navigateToMyPrizes: action,
      getAccountLink: action,
      setZendeskChatBotVisibility: action,
    });

    if (typeof window === 'object') {
      if ((window as any)[UI_STORE_KEY_FOR_WINDOW_OBJECT]) {
        console.warn(
          'A UI store was already registered. The old instance will be overwritten.',
        );
      }
      (window as any)[UI_STORE_KEY_FOR_WINDOW_OBJECT] = this;
    }
  }

  displayToast = (toast: Toast | null) => {
    this.toast = toast;
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    if (toast) {
      this.toastTimeout = setTimeout(() => {
        this.toast = null;
        this.toastTimeout = undefined;
      }, toast.timeout);

      return this.toastTimeout;
    }
  };

  displayHeaderBanner = (banner: HeaderBanner | null) => {
    this.headerBanner = banner;
  };

  showDrawCounterInHeader = (show: boolean) => {
    this.drawCounterInHeader = show;
  };

  setEnterFlowProgress = (enterFlowProgress: EnterFlowProgress) => {
    this.enterFlowProgress = enterFlowProgress;
  };

  setShowVideoOverlay = (show?: boolean) => {
    this.showVideoOverlay = show ?? !this.showVideoOverlay;
  };

  setVideoOverlayLink = (link: string) => {
    this.videoOverlayLink = link;
  };

  setVideoOverlayType = (type: 'instagram' | 'youtube') => {
    this.videoOverlayType = type;
  };

  removeVideoOverlayLink = () => {
    this.videoOverlayLink = null;
  };

  getAccountLink = () => {
    return ACCOUNT_PAGE_LINK;
  };

  navigateToDashboard = () => {
    Router.push(DASHBOARD_LINK);
  };

  navigateToAccount = () => {
    Router.push(this.getAccountLink());
  };

  navigateToMyPrizes = () => {
    Router.push('/my-wins');
  };

  hydrate(data?: UIHydration) {
    if (data?.toast) {
      this.toast = data.toast;
    }
    if (data?.headerBanner) {
      this.headerBanner = data.headerBanner;
    }
    if (data?.drawCounterInHeader) {
      this.drawCounterInHeader = data.drawCounterInHeader;
    }
  }

  /** Controls whether the zendesk chat bot icon is visible. We maintain a list of reasonCodes
   * that could choose to hide it. If the count of reasonCodes is greater than zero then the
   * widget is hidden, otherwise it is visible (provided other pre-conditions are met, e.g.
   * logged in user)
   */
  setZendeskChatBotVisibility(reasonCode: string, show: boolean) {
    const lastZendeskChatBotHiddenReasonsLength = Object.keys(
      this.zendeskChatBotHiddenReasons,
    ).length;
    if (show) {
      delete this.zendeskChatBotHiddenReasons[reasonCode];
    } else {
      this.zendeskChatBotHiddenReasons[reasonCode] = true;
    }

    if (!window.zE) {
      // Create a placeholder Zendesk API that can store actions that will be run when the zendesk script
      // loads in
      const zE: ZendeskChatWidget = function (...args: unknown[]) {
        (zE._ = zE._ || []).push(args);
        // THERE WILL BE MERGE CONFLICTS WHEN WIN WITH FRIENDS IS MERGE. KEEP THE zE.t LINE
        zE.t = new Date();
        // THERE WILL BE MERGE CONFLICTS WHEN WIN WITH FRIENDS IS MERGE. KEEP THE zE.t LINE
      };
      // THERE WILL BE MERGE CONFLICTS WHEN WIN WITH FRIENDS IS MERGE. KEEP THE zEmbed LINE
      window.zEmbed = window.zE = zE;
      // THERE WILL BE MERGE CONFLICTS WHEN WIN WITH FRIENDS IS MERGE. KEEP THE zEmbed LINE
    }

    const zendeskChatBotHiddenReasonsLength = Object.keys(
      this.zendeskChatBotHiddenReasons,
    ).length;
    if (
      zendeskChatBotHiddenReasonsLength != lastZendeskChatBotHiddenReasonsLength
    ) {
      if (zendeskChatBotHiddenReasonsLength > 0) {
        window.zE('messenger', 'hide');
      } else {
        window.zE('messenger', 'show');
      }
    }
  }
}

export function getGlobalUiStore() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const uiStore: undefined | UIStore = (window as any)[
    UI_STORE_KEY_FOR_WINDOW_OBJECT
  ];
  return uiStore;
}
